import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { VideoPlayer } from '@bblabs/module-library/molecules';
import { addAlphaComponent } from '@bblabs/module-library/styles';
import { Replay } from '@bblabs/module-library/svgs';
import { EnvironmentHelper } from '@bblabs/module-library/classes';
import { Environment } from '@bblabs/module-library/constants';
import { VideoJsPlayer } from 'video.js';
import openRwvPopup from '../../functions/rwvShared';
import ReactionsEndScreen from './components/ReactionsEndScreen';

/**
 * Post a message to the parent window, if available.
 * @param data - the data to post to the parent
 * @param origin - the origin to restrict the post to, defaults to the current origin.
 */
async function postMessage(data: object, origin = window.location.origin) {
  window.parent?.postMessage(data, origin);
}

export type WebAppPlayerProps = {
  className?: string;
  thumbUrl: string;
  sources: { type: string; src: string }[];
  clientId: string;
  subscriptionId: string;
  subscriptionStatus: string;
  ctaTitle: string;
  ctaLink: string;
  // used for styling only
  // eslint-disable-next-line react/no-unused-prop-types
  ctaColor: string;
  environment: Uppercase<Environment>;
  eventListeners: {
    name: string;
    function: VoidFunction;
  }[];
  autoplay?: boolean;
  // used for styling only
  // eslint-disable-next-line react/no-unused-prop-types
  useAspectRatio: number;
  ignorePoster: boolean;
  subtitleFileSrc?: string;
  /** Video Played MetaData is this the same type as in VideoEditPage??? */
  videoPlayMetadata?: {
    videoViewId: string;
    videoId: string;
    jobId: string;
    browserViewerId: string;
  };
  reactionsEnabled?: boolean;
  userFirstName?: string;
};

/**
 * Wrap the module library video player with additional functionality.
 *
 * Extends the ML VideoPlayer to include tracking and handles data population
 */
const WebAppPlayer = ({
  className = '',
  thumbUrl,
  sources,
  clientId,
  subscriptionId,
  subscriptionStatus,
  ctaTitle,
  ctaLink,
  environment,
  eventListeners,
  autoplay = false,
  ignorePoster,
  subtitleFileSrc = undefined,
  videoPlayMetadata = undefined,
  reactionsEnabled = false,
  userFirstName = '',
}: WebAppPlayerProps) => {
  useEffect(() => {
    if (environment === 'DEV') {
      EnvironmentHelper.setEnvironment('dev');
    }
  }, [environment]);

  useEffect(() => {
    postMessage({ event: 'iframeAppLoaded' }, '*');
  }, []);

  // Video end screen functionality
  const defaultEndScreenButtons = [
    {
      text: 'Replay',
      callback: (player: VideoJsPlayer) => {
        player.play();
      },
      element: <Replay />,
      name: 'replay',
    },
  ];

  // Create End Screen Props
  const canShowReactionsEndScreen = Boolean(reactionsEnabled && userFirstName);
  const endScreenProps = {
    buttons: canShowReactionsEndScreen ? [] : defaultEndScreenButtons,
    endScreen: canShowReactionsEndScreen && (
      <ReactionsEndScreen
        videoOwner={userFirstName}
        likeButtonClickHandler={() => {
          postMessage({ event: 'endScreenClick', target: 'like' });
        }}
        replyButtonClickHandler={() => {
          postMessage({ event: 'endScreenClick', target: 'comment' });
        }}
        replyWithVideoButtonClickHandler={() => {
          if (!videoPlayMetadata) {
            console.error('videoPlayMetadata is undefined');
            return;
          }
          openRwvPopup({
            environment,
            replySent: () => postMessage({ event: 'endScreenClick', target: 'rwv' }),
            videoId: videoPlayMetadata.videoId,
            clientId,
            videoViewId: videoPlayMetadata.videoViewId,
            browserViewerId: videoPlayMetadata.browserViewerId,
            beid: videoPlayMetadata.jobId,
          });
        }}
        replayButtonClickHandler={() => {
          // post message to the current window which contains the video player
          // the `videoPlayer` state is not available when this component is created
          // and registered as a VJS component. Posting the message allows the VideoPlayer
          // component to handle the event for us...
          window.postMessage({ event: 'replay' }, window.location.origin);
        }}
      />
    ),
  };

  // Create cta props if video has a CTA
  let ctaBannerProps = undefined;
  if (ctaLink !== '') {
    ctaBannerProps = {
      title: ctaTitle,
      link: ctaLink,
    };
  }

  return (
    <div className={className}>
      <VideoPlayer
        shouldPlayInline
        playPauseMode
        sources={sources}
        ctaBannerProps={ctaBannerProps}
        endScreenProps={endScreenProps}
        eventListeners={eventListeners}
        autoplay={autoplay}
        thumbUrl={ignorePoster ? '' : thumbUrl}
        environment={environment}
        subscriptionId={subscriptionId}
        subscriptionStatus={subscriptionStatus}
        clientId={clientId}
        subtitleFileSrc={subtitleFileSrc}
        videoPlayMetadata={videoPlayMetadata}
      />
    </div>
  );
};

export default styled(WebAppPlayer)`
  div.video-js {
    min-width: unset;
  }

  ${
    '' /* TODO: determine if we still need this override here
    after new video landing page is launched - 2021-11-12 */
  }
  /* override ML VideoPlayer style until we figure out what we want posters to look like */
  .video-js.vjs-paused:not(.vjs-has-started).vjs-has-thumb .vjs-control-bar {
    visibility: visible;
    display: flex;
  }

  ${'' /* specifically override the override above when we are in no controls mode */}
  .custom.controls-disabled
    .video-js.vjs-paused:not(.vjs-has-started).vjs-has-thumb
    .vjs-control-bar {
    visibility: hidden;
    display: unset;
  }

  ${(props) => {
    if (props.ctaColor === '') {
      return '';
    }
    return `
      .vjs-cta-banner > div:first-child {
        background-color: ${addAlphaComponent(props.ctaColor, 0.9)};
        &:hover {
          background-color: ${props.ctaColor};
        }
      }`;
  }}

  ${(props) => {
    if (props.useAspectRatio === 0) {
      return `
        video-js.video-js.vjs-fluid {
          padding-top: 0;
          height: 100%;
        }`;
    }
    return `
      video-js.video-js.vjs-fluid {
        padding-top: ${props.useAspectRatio}%;
      }`;
  }}
`;
